import * as React from 'react';
import { ListItem } from '@mui/material';
import { CalendarRowSize, DragItemType, Photographer } from '../../BookingsCalendarTypes';
import Typography from '@mui/material/Typography';
import { debounce } from 'lodash';

type DropZone = {
    onDrop: (
        photographer: Photographer,
        index: number | null,
        isResized: boolean,
        endIndex?: number
    ) => void;
    photographer: Photographer;
    rowIndex: number;
    formattedDisplayTime: string;
    lastSlot: boolean;
    index: number;
    availableForDrop: boolean;
    isAvailableForResize: boolean;
    preventResize: boolean;
    isResizing: boolean;
    hasNewOrderInCalendar: boolean;
    hasNoEventsInCalendar: boolean;
    isActiveRow: boolean;
    onActiveRow: (activeRow: number) => void;
};

enum DisplayedTimeIndicators {
    fullHour = '00',
    halfHour = '30'
}

const listItemStyle = (
    formattedDisplayTime,
    rowIndex,
    lastSlot,
    isOver,
    availableForDrop,
    isAvailableForResize,
    preventResize,
    hasNewOrderInCalendar,
    hasNoEventsInCalendar,
    isActiveRow
) => {
    return {
        padding: 0,
        width: '6px',
        height:
            isActiveRow || hasNewOrderInCalendar
                ? CalendarRowSize.large
                : hasNoEventsInCalendar
                ? CalendarRowSize.small
                : CalendarRowSize.medium,
        borderLeft:
            isOver && availableForDrop && !preventResize
                ? '1px solid #333D45'
                : formattedDisplayTime.includes(DisplayedTimeIndicators.fullHour)
                ? '1px solid #E0E0E0'
                : formattedDisplayTime.includes(DisplayedTimeIndicators.halfHour)
                ? '1px dashed #E0E0E0'
                : '',
        borderBottom: '1px solid #E0E0E0',
        borderRight: lastSlot ? '1px solid #E0E0E0' : '',
        borderTop: rowIndex < 1 ? '1px solid #E0E0E0' : '',
        zIndex: isOver && availableForDrop && !preventResize ? 1000 : 2
    };
};

const TimelineCalendarDropZone = ({
    onDrop,
    photographer,
    rowIndex,
    formattedDisplayTime,
    lastSlot,
    index,
    availableForDrop,
    isAvailableForResize,
    preventResize,
    isResizing,
    hasNewOrderInCalendar,
    hasNoEventsInCalendar,
    isActiveRow,
    onActiveRow
}: DropZone) => {
    const [isOver, setIsOver] = React.useState<boolean>(false);
    const drop = React.useRef<HTMLElement>(null);

    React.useEffect(() => {
        if (isOver && !isActiveRow) {
            onActiveRow(rowIndex);
        }
    }, [isOver]);

    const ELAPSED_TIME = 90;

    const changeIsOver = () => {
        setIsOver(true);
    };

    const debounceDragging = debounce(changeIsOver, ELAPSED_TIME);

    const handleDragOver = (event) => {
        if ((availableForDrop && !preventResize) || (isAvailableForResize && !preventResize)) {
            event.preventDefault();
        }
        if (isOver && !availableForDrop && !preventResize) {
            event.dataTransfer.dropEffect = 'none';
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        onActiveRow(null);
        let type = event.dataTransfer.getData('type');

        if (type === DragItemType.Resize && isAvailableForResize && !preventResize) {
            onDrop(photographer, null, true, index);
            setIsOver((prevState) => false);
        }
        if (type === DragItemType.Drag && availableForDrop) {
            onDrop(photographer, index, false);
            setIsOver((prevState) => false);
        }
        debounceDragging.cancel();
        if (isOver) {
            setIsOver((prevState) => false);
        }
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        if (!preventResize && !isResizing) {
            debounceDragging();
        }
        if (isOver) {
            setIsOver(false);
        }
    };

    const handleDragLeave = () => {
        debounceDragging.cancel();
        if (isOver) {
            setIsOver(false);
        }
    };

    const handleDragEnd = () => {
        debounceDragging.cancel();
        if (isOver) {
            setIsOver(false);
        }
    };

    return (
        <ListItem
            ref={drop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            aria-label={'time-slot'}
            key={photographer.id + rowIndex}
            id={index}
            sx={listItemStyle(
                formattedDisplayTime,
                rowIndex,
                lastSlot,
                isOver,
                availableForDrop,
                isAvailableForResize,
                preventResize,
                hasNewOrderInCalendar,
                hasNoEventsInCalendar,
                isActiveRow
            )}
        >
            {isOver && availableForDrop && !preventResize ? (
                <Typography
                    sx={{
                        backgroundColor: '#333D45',
                        color: 'white',
                        position: 'absolute',
                        top: '-32px',
                        padding: '4px',
                        borderRadius: '4px'
                    }}
                >
                    {formattedDisplayTime}
                </Typography>
            ) : null}
        </ListItem>
    );
};

export default React.memo(TimelineCalendarDropZone);
